import { c as _c } from "react/compiler-runtime";
import { add, differenceInSeconds, setMilliseconds, startOfDay, startOfHour, sub } from 'date-fns';
import { Formik, useField } from 'formik';
import { EventListFiltersProps } from '../../../../typings/EventList.interface';
import { RequestListFiltersProps } from '../../../../typings/RequestList.interface';
import { chart_resolutions, getBestResolution, getTime, startOfTime } from '../../../../utils/chart';
import { formatDate, RelativeDate, resolveRelativeDate } from '../../../../utils/date';
import { useDatePreferences, useUpdateTimezonePreference } from '../../../hooks/useDatePreferences';
import Button from '../../base/Button';
import { StyledCardSection } from '../../base/Card';
import DatePickerInput, { formatDateFilterValues } from '../../Form/Fields/DatePickerInput';
import { BROWSER_TIMEZONE_OPTION, resolveTimezoneOption } from '../../Form/Fields/TimezoneSelectInput';
import { Div } from '../../helpers/StyledUtils';
const DateFilterVisible: React.FC<{
  filters: EventListFiltersProps | RequestListFiltersProps;
  retention_days: number;
  onSubmit: () => void;
}> = t0 => {
  const $ = _c(23);
  const {
    retention_days,
    onSubmit
  } = t0;
  const {
    browser_timezone,
    timezone,
    uses_browser_timezone
  } = useDatePreferences();
  const updateTimezonePreference = useUpdateTimezonePreference();
  const [, t1, t2] = useField("date");
  const {
    value
  } = t1;
  const {
    setValue,
    setTouched
  } = t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== retention_days || $[1] !== timezone || $[2] !== value) {
    const new_date = {
      ...value
    };
    if (new_date?.relative) {
      const {
        min,
        max
      } = resolveRelativeDate(new_date.relative);
      new_date.min = min.toISOString();
      new_date.max = max.toISOString();
    }
    const now = new Date();
    const end_date = setMilliseconds(new_date?.max ? new Date(new_date?.max) : add(startOfHour(now), {
      hours: 1
    }), 0);
    const start_date = setMilliseconds(new_date?.min ? new Date(new_date?.min) : startOfDay(sub(now, {
      seconds: retention_days * 24 * 60 * 60
    })), 0);
    let [start_date_0, end_date_0] = [start_date, end_date];
    const diff = differenceInSeconds(end_date_0, start_date_0);
    const {
      chart_resolution
    } = {
      chart_resolution: getBestResolution(diff, 60, chart_resolutions).best
    };
    if (chart_resolution.granularity !== "second") {
      const granularity_key = `${chart_resolution.granularity}s`;
      end_date_0 = add(startOfTime[chart_resolution.granularity](end_date_0), {
        [granularity_key]: Math.floor((getTime[chart_resolution.granularity](end_date_0) + 1) / chart_resolution.unit) * chart_resolution.unit
      });
      start_date_0 = sub(end_date_0, {
        seconds: diff
      });
    }
    t3 = {
      ...value,
      min: value?.min || start_date_0.toISOString(),
      max: value?.max || end_date_0.toISOString()
    } as {
      relative?: RelativeDate;
      min?: string;
      max?: string;
    };
    t4 = value?.min ? formatDate(value.min, "HH:mm:ss", timezone) : formatDate(start_date_0, "HH:mm:ss", timezone);
    t5 = value?.max ? formatDate(value.max, "HH:mm:ss", timezone) : formatDate(end_date_0, "HH:mm:ss", timezone);
    $[0] = retention_days;
    $[1] = timezone;
    $[2] = value;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  const t6 = uses_browser_timezone ? BROWSER_TIMEZONE_OPTION : timezone;
  let t7;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      date: t3,
      start_time: t4,
      end_time: t5,
      timezone: t6
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const form_initial_values = t7;
  let t8;
  if ($[11] !== browser_timezone || $[12] !== onSubmit || $[13] !== setTouched || $[14] !== setValue || $[15] !== updateTimezonePreference) {
    t8 = async v => {
      const selected_timezone = resolveTimezoneOption(v.timezone, browser_timezone);
      await updateTimezonePreference(v.timezone === BROWSER_TIMEZONE_OPTION ? null : v.timezone);
      const {
        start_date: start_date_1,
        end_date: end_date_1
      } = formatDateFilterValues({
        start_date: v.date.min,
        start_time: v.start_time,
        end_date: v.date.max,
        end_time: v.end_time
      }, selected_timezone);
      setTouched(true);
      setValue({
        ...v.date,
        min: start_date_1,
        max: end_date_1
      });
      onSubmit();
    };
    $[11] = browser_timezone;
    $[12] = onSubmit;
    $[13] = setTouched;
    $[14] = setValue;
    $[15] = updateTimezonePreference;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleFormSubmit = t8;
  let t9;
  if ($[17] !== retention_days) {
    t9 = props => <Div><StyledCardSection><DatePickerInput display_reset={false} name="date" time_format="hh:mm:ss" retention_days={retention_days} /><Div flex={{
          justify: "flex-end"
        }} p={{
          t: 0,
          x: 4,
          b: 4
        }}><Button primary={true} onClick={() => props.handleSubmit()}>Apply</Button></Div></StyledCardSection></Div>;
    $[17] = retention_days;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== form_initial_values || $[20] !== handleFormSubmit || $[21] !== t9) {
    t10 = <Formik initialValues={form_initial_values} onSubmit={handleFormSubmit}>{t9}</Formik>;
    $[19] = form_initial_values;
    $[20] = handleFormSubmit;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
export default DateFilterVisible;